import AgentForm from "@/components/agents/AgentForm";
import AgentsList from "@/components/agents/AgentsList";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import PaginationControl from "@/components/common/PaginationControl";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { agentService } from "@/services/agent.service/agent.api";
import { teamService } from "@/services/team.service/team.api";
import useStore from "@/store/useStore";
import { AgentRole, AgentStatus, type Agent, type Team } from "@/types/team";
import { Bot, Search, User, UserPlus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export default function Agents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isCreateAgentDialogOpen, setIsCreateAgentDialogOpen] = useState(false);
  const [isEditAgentDialogOpen, setIsEditAgentDialogOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState<Partial<Agent>>({
    permissions: [AgentRole.APP_AGENT],
    status: AgentStatus.ACTIVE,
    teams: [],
  });
  const [newAgentPassword, setNewAgentPassword] = useState("");

  // Estados para controle de loading de botões
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [isEditingAgent, setIsEditingAgent] = useState(false);

  // Filtros e paginação
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Loading state usando o estado global
  const loading = useStore((state) => state.commonSlice.loaders.getAllAgents);

  // Buscar agentes
  const fetchAgents = useCallback(async () => {
    // Determinar os parâmetros de filtragem baseados nos filtros ativos
    const queryParams: {
      search?: { s_name: string };
      s_and_status?: string;
    } = {};

    // Adicionar busca se houver texto de pesquisa
    if (searchQuery.trim()) {
      queryParams.search = { s_name: searchQuery.trim() };
    }

    // Adicionar filtro de status
    if (activeFilter === "active") {
      queryParams.s_and_status = "active";
    } else if (activeFilter === "inactive") {
      queryParams.s_and_status = "inactive";
    }

    const [res, error] = await agentService.getAll(queryParams, page, limit);

    if (error) {
      console.error("Erro ao buscar agentes:", error);
      toast.error("Erro ao buscar agentes");
      return;
    }

    if (res) {
      console.log("Agentes recebidos da API:", res);

      if (Array.isArray(res.result)) {
        setAgents(res.result);
      } else {
        console.error("Formato de resposta inesperado:", res.result);
        setAgents([]);
      }

      setTotalPages(res.qtdPages);
      setTotalItems(res.total);
    }
  }, [searchQuery, activeFilter, page, limit]);

  // Buscar equipes para o select
  const fetchTeams = useCallback(async () => {
    const [res, error] = await teamService.getAll({}, 1, 100);

    if (error) {
      console.error("Erro ao buscar equipes:", error);
      toast.error("Erro ao buscar equipes");
      return;
    }

    if (res && Array.isArray(res.result)) {
      console.log("Equipes carregadas para o select:", res.result);
      setTeams(res.result);
    } else {
      console.error("Formato de resposta inesperado para equipes:", res);
      setTeams([]);
    }
  }, []);

  // Funções para gerenciar agentes
  const getTeamName = (teamId: string) => {
    const team = teams.find((team) => team._id === teamId);
    return team ? team.name : "Sem equipe";
  };

  // Criar agente (ou bot — mesmo fluxo, currentAgent.isBot diferencia)
  const handleCreateAgent = async () => {
    if (!currentAgent.name || !currentAgent.email) {
      toast.error("Nome e email são obrigatórios");
      return;
    }

    if (!currentAgent.permissions?.includes(AgentRole.APP_ADMIN) && (!currentAgent.teams || currentAgent.teams.length === 0)) {
      toast.error("Pelo menos uma equipe é obrigatória para agentes não administradores");
      return;
    }

    setIsCreatingAgent(true);

    try {
      await agentService.create(currentAgent);
      setCurrentAgent({
        permissions: [AgentRole.APP_AGENT],
        status: AgentStatus.ACTIVE,
        teams: [],
      });
      setIsCreateAgentDialogOpen(false);
    } catch (error) {
      console.error("Erro ao criar agente:", error);
      toast.error("Erro ao criar agente");
    } finally {
      setIsCreatingAgent(false);
    }

    fetchAgents();
  };

  // Editar agente
  const handleEditAgent = async () => {
    if (!currentAgent._id || !currentAgent.name || !currentAgent.email) {
      toast.error("Nome e email são obrigatórios");
      return;
    }

    if (newAgentPassword && newAgentPassword.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres");
      return;
    }

    setIsEditingAgent(true);

    try {
      const [, updateError] = await agentService.update(
        currentAgent._id,
        {
          name: currentAgent.name,
          email: currentAgent.email,
          teams: currentAgent.teams,
          permissions: currentAgent.permissions,
          status: currentAgent.status,
          phone: currentAgent.phone,
        }
      );

      if (updateError) {
        return;
      }

      if (newAgentPassword) {
        const [, passwordError] = await agentService.adminChangePassword(
          currentAgent._id,
          newAgentPassword
        );
        if (passwordError) {
          return;
        }
        toast.success("Agente atualizado e senha alterada com sucesso");
      }

      setNewAgentPassword("");
      setIsEditAgentDialogOpen(false);
    } catch (error) {
      console.error("Erro ao atualizar agente:", error);
      toast.error("Erro ao atualizar agente");
    } finally {
      setIsEditingAgent(false);
    }

    fetchAgents();
  };

  // Alternar status do agente (ativo/inativo)
  const handleToggleAgentStatus = async (agentId: string) => {
    const agent = agents.find((a) => a._id === agentId);
    if (!agent) return;

    try {
      await agentService.updateStatus(
        agentId,
        agent.status === AgentStatus.ACTIVE ? AgentStatus.INACTIVE : AgentStatus.ACTIVE
      );
    } catch (error) {
      console.error("Erro ao atualizar status do agente:", error);
      toast.error("Erro ao atualizar status do agente");
    }

    fetchAgents();
  };

  // Paginação
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  };

  // Carregar dados iniciais
  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Agentes</h1>
        <div className="flex gap-2">
          <Drawer
            open={isCreateAgentDialogOpen}
            onOpenChange={setIsCreateAgentDialogOpen}
          >
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  setCurrentAgent({
                    permissions: [AgentRole.APP_AGENT],
                    status: AgentStatus.ACTIVE,
                    teams: [],
                    isBot: true,
                  });
                  setIsCreateAgentDialogOpen(true);
                }}
              >
                <Bot className="h-4 w-4" />
                Novo Bot
              </Button>
              <DrawerTrigger asChild>
                <Button
                  className="gap-2"
                  onClick={() =>
                    setCurrentAgent({
                      permissions: [AgentRole.APP_AGENT],
                      status: AgentStatus.ACTIVE,
                      teams: [],
                      isBot: false,
                    })
                  }
                >
                  <UserPlus className="h-4 w-4" />
                  Novo Agente
                </Button>
              </DrawerTrigger>
            </div>
            <DrawerContent className="sm:max-w-xl">
              <div className="px-6 py-4">
                <DrawerHeader>
                  <DrawerTitle>{currentAgent.isBot ? "Criar Bot" : "Criar Agente"}</DrawerTitle>
                  <DrawerDescription>
                    {currentAgent.isBot
                      ? "Um bot não loga sozinho — existe só pra portar um token MCP, gerado logo após criar."
                      : "Preencha as informações do novo agente"}
                  </DrawerDescription>
                </DrawerHeader>
                <AgentForm
                  agent={currentAgent}
                  teamsSended={teams}
                  setAgent={setCurrentAgent}
                  onSave={handleCreateAgent}
                  onCancel={() => setIsCreateAgentDialogOpen(false)}
                  isCreate={true}
                  isLoading={isCreatingAgent}
                />
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Buscar por nome, email ou telefone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1.5 h-7 w-7"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <Tabs
            defaultValue="all"
            className="w-full md:w-auto"
            onValueChange={(value) =>
              setActiveFilter(value as "all" | "active" | "inactive")
            }
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="active">Ativos</TabsTrigger>
              <TabsTrigger value="inactive">Inativos</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <LoadingOverlay isLoading={loading} />

      {!loading && (
        <>
          <AgentsList
            agents={agents}
            getTeamName={getTeamName}
            onEditAgent={(agent) => {
              setCurrentAgent(agent);
              setNewAgentPassword("");
              setIsEditAgentDialogOpen(true);
            }}
            onToggleStatus={handleToggleAgentStatus}
          />

          {agents.length === 0 && (
            <div className="flex flex-col items-center justify-center p-12 bg-muted/30 rounded-lg mt-4">
              <User className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                Nenhum agente encontrado
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setIsCreateAgentDialogOpen(true)}
              >
                Criar Agente
              </Button>
            </div>
          )}

          <div className="mt-4">
            <PaginationControl
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={limit}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </>
      )}

      {/* Edit Agent Dialog */}
      <Drawer
        open={isEditAgentDialogOpen}
        onOpenChange={setIsEditAgentDialogOpen}
      >
        <DrawerContent className="sm:max-w-xl">
          <div className="px-6 py-4">
            <DrawerHeader>
              <DrawerTitle>{currentAgent.isBot ? "Editar Bot" : "Editar Agente"}</DrawerTitle>
              <DrawerDescription>
                {currentAgent.isBot
                  ? "Gerencie os dados e o token MCP deste bot"
                  : "Atualize as informações do agente"}
              </DrawerDescription>
            </DrawerHeader>
            <AgentForm
              agent={currentAgent}
              teamsSended={teams}
              setAgent={setCurrentAgent}
              onSave={handleEditAgent}
              onCancel={() => {
                setNewAgentPassword("");
                setIsEditAgentDialogOpen(false);
              }}
              isLoading={isEditingAgent}
              newPassword={newAgentPassword}
              onNewPasswordChange={setNewAgentPassword}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
